import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Package, SignOut, Storefront, UserCircle } from '@phosphor-icons/react';
import { Icon } from '../Icon';
/* Deep import rather than the `complex` barrel, for the cycle reason spelled
   out at the top of TopNav.jsx. */
import { Tooltip } from '../desktop/complex/Tooltip';
import { cn } from '../../lib/cn';
import { useAuth } from '../../../hooks/useAuth';
import { routes } from '../../../routes/routes';
import { track } from '../../../analytics/track';
import { EVENTS } from '../../../analytics/events';
import { MY_ORDERS_TO } from './navModel';

/** Two letters from a name, or a single letter from an email, or nothing. */
export function initialsFor(user) {
  if (!user) return '';
  const name = user.full_name || user.name || '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  if (user.email) return user.email.slice(0, 1).toUpperCase();
  return '';
}

/**
 * The avatar and its menu, the last thing in the MarketShell top nav.
 *
 * Auth comes from the app's own `useAuth`, which reads the Redux auth slice, so
 * this invents no session state. Signed out, the avatar is replaced by a Sign
 * in command, because an avatar for nobody is a lie.
 *
 * The panel is a `.dsk-popover`, one of the few things allowed a shadow, and it
 * closes on outside click and on Escape.
 *
 * @param {{ className?: string }} props
 */
export function UserMenu({ className }) {
  const navigate = useNavigate();
  const { user, isAuthenticated, isSeller, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;

    const onDocumentDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('mousedown', onDocumentDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const go = (to) => {
    close();
    navigate(to);
  };

  if (!isAuthenticated) {
    return (
      <button
        type="button"
        className={cn('dsk-btn', 'dsk-btn--primary', 'dsk-btn--sm', className)}
        onClick={() => navigate(routes.LOGIN)}
      >
        Sign in
      </button>
    );
  }

  const name = user?.full_name || user?.name || 'Your account';

  return (
    <div className={cn('dsk-navmenu', className)} ref={wrapRef}>
      {/* The fourth tooltip in the nav cluster, and it lives here rather than
          in TopNav because the trigger is inside this component and `Tooltip`
          needs a real element to measure. `disabled` while the menu is open
          stops it coming BACK on the way out of the panel: a tip reading
          "Account" over the open panel is annotating something already on
          screen, and moving between the two is the common path. */}
      <Tooltip value="Account" disabled={open} placement="bottom">
        <button
          type="button"
          className="dsk-navmenu__trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Account menu"
          onClick={() => setOpen((value) => !value)}
        >
          <span className="dsk-avatar" aria-hidden="true">
            {initialsFor(user)}
          </span>
        </button>
      </Tooltip>

      {open && (
        <div className="dsk-popover dsk-navmenu__panel" role="menu">
          <div className="dsk-navmenu__head">
            <div className="dsk-navmenu__name">{name}</div>
            {user?.email && <div className="dsk-navmenu__meta">{user.email}</div>}
          </div>

          <button
            type="button"
            role="menuitem"
            className="dsk-menu-item"
            onClick={() => go(routes.PROFILE)}
          >
            <Icon as={UserCircle} />
            Profile
          </button>
          {/* Collection is in the account menu AND on the header heart, which
              is not a duplicate: the heart is the shortcut for someone who is
              already browsing, this is the answer for someone who went looking
              under their account for the cards they saved — which is where a
              buyer looks when they do not know the heart is a link. Same
              destination, two paths, one word on both. */}
          <button
            type="button"
            role="menuitem"
            className="dsk-menu-item"
            onClick={() => go(routes.COLLECTION)}
          >
            <Icon as={Heart} />
            Collection
          </button>
          <button
            type="button"
            role="menuitem"
            className="dsk-menu-item"
            onClick={() => go(routes.DASHBOARD)}
          >
            <Icon as={Storefront} />
            Seller dashboard
          </button>
          {!isSeller && (
            <button
              type="button"
              role="menuitem"
              className="dsk-menu-item"
              onClick={() => {
                track(EVENTS.BECOME_SELLER_CLICKED, { source: 'user_menu' });
                go(routes.SELLER_REGISTRATION);
              }}
            >
              <Icon as={Storefront} />
              Become a seller
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="dsk-menu-item"
            /* The buyer's ORDERS, not `/order-tracking`. That route is a
               track-by-reference lookup that opens on an empty field asking
               for a tracking ID — useful, but it is not "my orders", and
               sending someone there from a menu item called Orders is the
               same mix-up the mobile tab bar had. */
            onClick={() => go(MY_ORDERS_TO)}
          >
            <Icon as={Package} />
            Orders
          </button>
          <hr className="dsk-divider" />
          <button
            type="button"
            role="menuitem"
            className="dsk-menu-item"
            onClick={() => {
              close();
              logout();
            }}
          >
            <Icon as={SignOut} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
